import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteNarrativeById, patchNarrativeById } from '@/api/generated/narrative/narrative';

interface UseNarrativeMutationsOptions {
  onDeleteError?: (error: Error) => void;
  onDeleteSuccess?: () => void;
  onUpdateError?: (error: Error) => void;
  onUpdateSuccess?: () => void;
}

export function useNarrativeMutations({
  onDeleteError,
  onDeleteSuccess,
  onUpdateError,
  onUpdateSuccess,
}: UseNarrativeMutationsOptions = {}) {
  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: ({ id, summary }: { id: string; summary: string }) =>
      patchNarrativeById(id, { summary }),
    onError: (error: Error) => {
      onUpdateError?.(error);
    },
    // Prefix invalidation hits both rolling (['narrative','list',KIND]) and
    // custom per-job (['narrative','list','CUSTOM',jobId]) caches.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['narrative', 'list'] });
      onUpdateSuccess?.();
    },
    retry: false,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteNarrativeById(id),
    onError: (error: Error) => {
      onDeleteError?.(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['narrative', 'list'] });
      onDeleteSuccess?.();
    },
    retry: false,
  });

  return {
    delete: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
    isUpdating: updateMutation.isPending,
    update: updateMutation.mutate,
  };
}
